
import * as React from "react";
import {t} from "i18next";

import {Step, Icon, Table, Label, Flag, Segment} from "semantic-ui-react";
import {OptionTextDto, OptionDto} from "src/services/Client";
import {OptionTransferFailureDto} from "src/services/OptionTransferDtos";
import DataAdapterOptionsView from "../DataAdapterOptionsView";
import PromtFormatter from "src/components/Table/components/reactQuill/PromtFormatter";

export enum StepTab {
    InitTab = 0,
    SummaryTab = 1,
    ConfirmTab = 2,
    ResultTab = 3
}

export enum SelectAction {
    SingleSelect = 0,
    SelectAll = 1,
    DeselectAll = 2
}

export interface IAMTBOverviewPage {
    items: DataAdapterOptionsView[];
    total: number;
}

export const AMTB_OVERVIEW_PAGE_SIZE = 10;

export const isInitialWorkflowStep = (item: DataAdapterOptionsView): boolean =>
    !!item.statusValueStates && item.statusValueStates.length > 0 && item.statusValueState === item.statusValueStates[0].id;

export const isLastWorkflowStep = (item: DataAdapterOptionsView): boolean => {
    const workflow = (item.workflows || []).find((p) => p.name === item.workFlowName);
    if (!workflow || !workflow.status) {
        return false;
    }
    const current = workflow.status.find((p) => p.objectId === item.statusValueState);
    return !!current && current.sequenceNumber === 5;
};

interface StepHeaderProps {
    activeTab: StepTab;
}

export interface AMTBTransferErrorTableProps {
    result?: {[key: number]: string};
    items?: OptionDto[];
    failures?: OptionTransferFailureDto[];
}

export interface IErrorRow {
    optionId: number;
    optionCode?: string;
    optionType?: string;
    texts?: OptionTextDto[];
    message?: string;
}

export const toErrorRows = (props: AMTBTransferErrorTableProps): IErrorRow[] => {
    const items = props.items || [];
    const findDto = (id: number) => items.find((a) => a.objectId === id);
    if (props.failures) {
        return props.failures.map((f) => {
            const dto = findDto(f.optionId);
            return {
                optionId: f.optionId,
                optionCode: f.optionCode || (dto && dto.optionBasic ? dto.optionBasic.optionCode : undefined),
                optionType: f.optionType || (dto && dto.optionBasic ? dto.optionBasic.optionType : undefined),
                texts: dto ? dto.texts : undefined,
                message: f.message
            };
        });
    }
    const result = props.result || {};
    return Object.keys(result).map((key: string) => {
        const id = Number(key);
        const dto = findDto(id);
        return {
            optionId: id,
            optionCode: dto && dto.optionBasic ? dto.optionBasic.optionCode : undefined,
            optionType: dto && dto.optionBasic ? dto.optionBasic.optionType : undefined,
            texts: dto ? dto.texts : undefined,
            message: result[key]
        };
    });
};

export const renderOptionTexts = (texts?: OptionTextDto[]) => (
    <Segment.Group horizontal={true}>
        {(texts || []).sort((a, b) =>
            (a.language && b.language && a.language.code && b.language.code && a.language.code > b.language.code) ? 1 : -1)
            .map((txt: OptionTextDto, idx: number) =>
                <Segment
                    key={"txt-" + idx}
                    className={'amtb-transfer-text-segment'}
                >
                    <Flag
                        name={txt.language && txt.language.code === "de" ? "de" : "gb"}
                        className={'amtb-transfer-text-flag'}
                    />
                    {txt.shorttext || ""}
                </Segment>
            )}
    </Segment.Group>
);

export const StepHeader = (props: StepHeaderProps) => (
    <Step.Group size={'mini'} className={'width--100Percent'}>
        <Step active={props.activeTab === StepTab.InitTab}>
            <Icon name={'pencil'} />
            <Step.Content>
                <Step.Title>{t("AMTB_TRANSFER:SELECT_MTB_TT")}</Step.Title>
            </Step.Content>
        </Step>

        <Step active={props.activeTab === StepTab.SummaryTab}>
            <Icon name={'tasks'} />
            <Step.Content>
                <Step.Title>{t("AMTB_TRANSFER:SELECT_ITEMS_TT")}</Step.Title>
            </Step.Content>
        </Step>

        <Step active={props.activeTab === StepTab.ConfirmTab}>
            <Icon name={'checkmark'} />
            <Step.Content>
                <Step.Title>{t("AMTB_TRANSFER:CONFIRM_TRANSFER_TT")}</Step.Title>
            </Step.Content>
        </Step>
        <Step active={props.activeTab === StepTab.ResultTab}>
            <Icon name={'info'} />
            <Step.Content>
                <Step.Title>{t("AMTB_TRANSFER:TRANSFER_RESULT_TT")}</Step.Title>
            </Step.Content>
        </Step>
    </Step.Group>
);

export const AMTBTransferErrorTable = (props: AMTBTransferErrorTableProps) => {
    const rows = toErrorRows(props);
    return (
        <>
            <p style={{marginBottom: '10px', color: 'red'}}><b>{rows.length + ' '}</b> {t("AMTB_TRANSFER:RESULT_ERROR")}</p>
            <Table
                celled={true}
                structured={true}
                className={"text-align--center"}
            >
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>{t("AMTB_TRANSFER:OPTION_IDENTIFIER")}</Table.HeaderCell>
                        <Table.HeaderCell>{t("AMTB_TRANSFER:SHORTTEXT")}</Table.HeaderCell>
                        <Table.HeaderCell>{t("AMTB_TRANSFER:ERROR_MSG")}</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {rows.map((row: IErrorRow, index: number) => (
                        <Table.Row key={'row-' + index}>
                            <Table.Cell>
                                <Label size={'large'} className={'whitespace--nowrap'}>
                                    {row.optionCode || ('#' + row.optionId)}
                                    {row.optionType &&
                                        <Label.Detail>
                                            {row.optionType}
                                        </Label.Detail>
                                    }
                                </Label>
                            </Table.Cell>
                            <Table.Cell>
                                {row.texts && row.texts.length > 0 ? renderOptionTexts(row.texts) : '–'}
                            </Table.Cell>
                            <Table.Cell>
                                {row.message}
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </>
    );
};



// WEBPACK FOOTER //
// ./src/views/OptionsView/amtbTransfer/AMTBTransferUtils.tsx